import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { ErrorState, Pagination } from '@/components/common';
import { formatDateTime, formatRelative } from '@/shared/lib/format';
import { humanise } from '@/lib/format';
import { ticketPath } from '@/constants';
import { useTicketCounts, useTickets } from './queries';
import { TICKET_PRIORITIES, type TicketListQuery, type TicketView } from './types';

const PAGE_SIZE = 25;
const ANY = 'any';

/**
 * Every lab's support tickets.
 *
 * Opens on the active queue, oldest first - a ticket that has waited longest is
 * the one to pick up. "Needs reply" marks the ones where the lab spoke last.
 */
export function TicketQueue() {
  const navigate = useNavigate();
  const [view, setView] = useState<TicketView>('active');
  const [priority, setPriority] = useState<string>(ANY);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const query: TicketListQuery = {
    view,
    limit: PAGE_SIZE,
    offset,
    ...(priority !== ANY ? { priority: priority as TicketListQuery['priority'] } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  };

  const tickets = useTickets(query);
  const counts = useTicketCounts();

  const tiles = [
    { label: 'Needs a reply', value: counts.data?.awaitingStaff },
    { label: 'Open', value: counts.data?.open },
    { label: 'In progress', value: counts.data?.inProgress },
    { label: 'Waiting on lab', value: counts.data?.waitingOnUser },
    { label: 'Resolved, last 24h', value: counts.data?.resolvedLast24h },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-lg border bg-card px-4 py-3">
            <p className="text-[11.5px] text-muted-foreground">{tile.label}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums">
              {tile.value ?? <Skeleton className="h-6 w-8" />}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={view}
          onValueChange={(value) => {
            setView(value as TicketView);
            setOffset(0);
          }}
        >
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <Select
          value={priority}
          onValueChange={(value) => {
            setPriority(value);
            setOffset(0);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All priorities</SelectItem>
            {TICKET_PRIORITIES.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
          placeholder="Search subject, lab or person"
          aria-label="Search tickets"
          className="w-full sm:w-72"
        />
      </div>

      {tickets.isError && (
        <ErrorState
          title="Could not load tickets"
          description={tickets.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void tickets.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ticket</TableHead>
              <TableHead className="hidden md:table-cell w-48">Lab</TableHead>
              <TableHead className="w-28">Priority</TableHead>
              <TableHead className="w-36">Status</TableHead>
              <TableHead className="hidden lg:table-cell w-40">Last activity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.isPending &&
              Array.from({ length: 6 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {tickets.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  {view === 'active' && !search && priority === ANY
                    ? 'No tickets waiting. Nice.'
                    : 'No tickets match these filters.'}
                </TableCell>
              </TableRow>
            )}

            {tickets.data?.data.map((ticket) => (
              <TableRow
                key={ticket.id}
                className="cursor-pointer"
                onClick={() => {
                  void navigate(ticketPath(ticket.id));
                }}
              >
                <TableCell className="max-w-md">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {ticket.ref}
                    </span>
                    {ticket.awaitingStaff && (
                      <span className="rounded bg-pill-caution px-1.5 py-0.5 text-[10.5px] font-semibold text-pill-caution-foreground">
                        Needs reply
                      </span>
                    )}
                  </div>
                  <Link
                    to={ticketPath(ticket.id)}
                    className="font-medium hover:underline"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {ticket.subject}
                  </Link>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {humanise(ticket.category)}
                    {ticket.raisedBy ? ` · ${ticket.raisedBy.name}` : ''}
                    {ticket.commentCount > 0
                      ? ` · ${String(ticket.commentCount)} repl${ticket.commentCount === 1 ? 'y' : 'ies'}`
                      : ''}
                  </p>
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">
                  {ticket.organization.name}
                </TableCell>
                <TableCell>
                  <StatusPill status={ticket.priority} minWidth={64} />
                </TableCell>
                <TableCell>
                  <StatusPill status={ticket.status} />
                </TableCell>
                <TableCell
                  className="hidden lg:table-cell text-muted-foreground tabular-nums"
                  title={formatDateTime(ticket.lastActivityAt)}
                >
                  {formatRelative(ticket.lastActivityAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      {tickets.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={tickets.data.data.length}
          total={tickets.data.meta.total}
          hasMore={tickets.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
